import React, { useState, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { Activity, CheckCircle, Archive } from 'lucide-react';
import { STATUS_COLORS } from '../utils/constants';
import './PinBoard.css';

const PANEL_GROUPS = [
  {
    id: 'pipeline',
    label: 'Active Pipeline',
    icon: <Activity size={14} />,
    statuses: ['Applied', 'Screening', 'Interview', 'Assessment'],
  },
  {
    id: 'outcome',
    label: 'Outcomes',
    icon: <CheckCircle size={14} />,
    statuses: ['Offer', 'Accepted'],
  },
  {
    id: 'closed',
    label: 'Closed',
    icon: <Archive size={14} />,
    statuses: ['Rejected', 'Withdrawn', 'Ghosted'],
  },
];

const ALL_STATUSES = PANEL_GROUPS.flatMap(g => g.statuses);
const PinBoard = ({ jobs, onCardClick, onStatusChange }) => {
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const touchDragRef = useRef(null);

  const grouped = {};
  ALL_STATUSES.forEach(s => { grouped[s] = []; });
  jobs.forEach(j => {
    if (grouped[j.status] !== undefined) grouped[j.status].push(j);
    else grouped['Applied'].push(j);
  });

  const handleDragStart = useCallback((e, job) => {
    setDragging({ jobId: job._id, sourceStatus: job.status });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job._id);

    // Clone the actual post-it card as the drag ghost image
    const card = e.currentTarget;
    const clone = card.cloneNode(true);
    const w = card.offsetWidth;
    const h = card.offsetHeight;

    Object.assign(clone.style, {
      position: 'fixed',
      top: '-9999px',
      left: '-9999px',
      width: `${w}px`,
      height: `${h}px`,
      transform: 'rotate(3deg) scale(1.04)',
      pointerEvents: 'none',
      zIndex: '99999',
      boxShadow: '0 12px 40px rgba(37,99,235,0.22), 0 4px 12px rgba(0,0,0,0.15)',
      borderRadius: '3px 12px 12px 12px',
      // Remove the faded dragging class that might be on the original
      opacity: '0.9',
    });

    document.body.appendChild(clone);
    // Place the ghost under the cursor — offset so cursor is in the top-left area of card
    e.dataTransfer.setDragImage(clone, 24, 24);
    // Remove clone after browser has captured the ghost image (next tick)
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (document.body.contains(clone)) {
          document.body.removeChild(clone);
        }
      }, 0);
    });
  }, []);

  const handleDragOver = useCallback((e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(status);
  }, []);

  const handleDrop = useCallback((e, targetStatus) => {
    e.preventDefault();
    if (dragging && dragging.sourceStatus !== targetStatus) {
      onStatusChange(dragging.jobId, targetStatus);
    }
    setDragging(null);
    setDragOver(null);
  }, [dragging, onStatusChange]);

  const handleDragEnd = useCallback(() => {
    setDragging(null);
    setDragOver(null);
  }, []);

  const handleTouchDragStart = useCallback((e, job) => {
    const startX = e.touches[0].clientX;
    const startY = e.touches[0].clientY;
    let dragStarted = false;

    const handleMove = (e) => {
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - startX);
      const dy = Math.abs(touch.clientY - startY);
      if (!dragStarted) {
        if (dx < 6 && dy < 6) return;
        if (dy > dx) { cleanup(); return; } // vertical scroll intent
        dragStarted = true;
        touchDragRef.current = { jobId: job._id, sourceStatus: job.status };
        setDragging({ jobId: job._id, sourceStatus: job.status });
      }
      e.preventDefault();
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const col = el?.closest('[data-status]');
      setDragOver(col ? col.dataset.status : null);
    };

    const handleEnd = (e) => {
      if (dragStarted && touchDragRef.current) {
        const touch = e.changedTouches[0];
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        const col = el?.closest('[data-status]');
        if (col) {
          const targetStatus = col.dataset.status;
          if (targetStatus !== touchDragRef.current.sourceStatus) {
            onStatusChange(touchDragRef.current.jobId, targetStatus);
          }
        }
      }
      cleanup();
    };

    const cleanup = () => {
      dragStarted = false;
      touchDragRef.current = null;
      setDragging(null);
      setDragOver(null);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }, [onStatusChange]);

  if (jobs.length === 0) {
    return (
      <div className="pinboard-root">
        <div className="pinboard-empty">
          <div className="pinboard-empty-icon"></div>
          <h3>No applications to pin</h3>
          <p>Add applications to see them on the board.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pinboard-root">
      <div className="pinboard-panels">
        {PANEL_GROUPS.map(group => (
          <PanelGroup
            key={group.id}
            group={group}
            grouped={grouped}
            dragging={dragging}
            dragOver={dragOver}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onCardClick={onCardClick}
            onTouchDragStart={handleTouchDragStart}
          />
        ))}
      </div>
    </div>
  );
};

const PanelGroup = ({ group, grouped, dragging, dragOver, onDragStart, onDragOver, onDrop, onDragEnd, onCardClick, onTouchDragStart }) => {
  const totalInGroup = group.statuses.reduce((sum, s) => sum + grouped[s].length, 0);

  return (
    <div className="panel-group">
      <div className="panel-group-header">
        <span className="panel-group-icon">{group.icon}</span>
        <span className="panel-group-label">{group.label}</span>
        <span className="panel-group-count">{totalInGroup}</span>
      </div>
      <div className="panel-group-cols">
        {group.statuses.map(status => (
          <StatusColumn
            key={status}
            status={status}
            jobs={grouped[status]}
            isDragOver={dragOver === status}
            draggingId={dragging?.jobId}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            onCardClick={onCardClick}
            onTouchDragStart={onTouchDragStart}
          />
        ))}
      </div>
    </div>
  );
};

const StatusColumn = ({ status, jobs, isDragOver, draggingId, onDragStart, onDragOver, onDrop, onDragEnd, onCardClick, onTouchDragStart }) => {
  const colors = STATUS_COLORS[status] || {};

  return (
    <div
      className={`pin-column ${isDragOver ? 'pin-column-drag-over' : ''}`}
      data-status={status}
      onDragOver={e => onDragOver(e, status)}
      onDrop={e => onDrop(e, status)}
      onDragLeave={e => {
        // Only clear if leaving the column entirely (not entering a child)
        if (!e.currentTarget.contains(e.relatedTarget)) {
          // dragOver will be reset by the next column's onDragOver
        }
      }}
      style={isDragOver ? { '--col-drag-border': colors.border, '--col-drag-bg': colors.bg } : {}}
    >
      <div className="pin-col-header" style={{ borderTopColor: isDragOver ? colors.border : colors.border }}>
        <span className="pin-col-status" style={{ color: colors.text }}>{status}</span>
        <span className="pin-col-count" style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
          {jobs.length}
        </span>
      </div>

      <div className="pin-col-body">
        {jobs.map(job => (
          <PostIt
            key={job._id}
            job={job}
            colors={colors}
            isDragging={draggingId === job._id}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onTouchDragStart={onTouchDragStart}
            onClick={() => onCardClick(job)}
          />
        ))}

        {jobs.length === 0 && (
          <div className={`pin-col-empty ${isDragOver ? 'pin-col-empty-active' : ''}`}>
            {isDragOver ? 'Drop here' : 'Empty'}
          </div>
        )}
      </div>
    </div>
  );
};

const PostIt = ({ job, colors, isDragging, onDragStart, onDragEnd, onTouchDragStart, onClick }) => {
  const fmtDate = (d) => {
    try { return format(new Date(d), 'MMM dd'); } catch { return ''; }
  };

  const formatSalary = (job) => {
    if (!job.salaryMin && !job.salaryMax) return null;
    const cur = job.salaryCurrency || 'PHP';
    const fmt = n => n >= 1000 ? `${(n / 1000).toFixed(0)}k` : n;
    if (job.salaryMin && job.salaryMax) {
      if (job.salaryMin === job.salaryMax) return `${cur} ${fmt(job.salaryMin)}`;
      return `${cur} ${fmt(job.salaryMin)}–${fmt(job.salaryMax)}`;
    }
    if (job.salaryMin) return `${cur} ${fmt(job.salaryMin)}+`;
    return `up to ${cur} ${fmt(job.salaryMax)}`;
  };

  const salary = formatSalary(job);

  return (
    <div
      className={`postit ${isDragging ? 'postit-dragging' : ''}`}
      style={{ '--pin-bg': colors.pin || '#F0F4FF', '--pin-border': colors.border }}
      draggable
      onDragStart={e => onDragStart(e, job)}
      onDragEnd={onDragEnd}
      onTouchStart={e => onTouchDragStart(e, job)}
      onClick={onClick}
    >
      {/* Pin dot at top */}
      <div className="postit-pin" style={{ background: colors.text }} />

      {/* Drag handle indicator */}
      <div className="postit-drag-handle" title="Drag to change status">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
          <circle cx="2" cy="2" r="1.2"/><circle cx="8" cy="2" r="1.2"/>
          <circle cx="2" cy="5" r="1.2"/><circle cx="8" cy="5" r="1.2"/>
          <circle cx="2" cy="8" r="1.2"/><circle cx="8" cy="8" r="1.2"/>
        </svg>
      </div>

      <div className="postit-company">{job.company}</div>
      <div className="postit-role">{job.role}</div>

      <div className="postit-tags">
        {job.workSetup && job.workSetup !== 'Not specified' && (
          <span className="postit-tag">
            {job.workSetup}
          </span>
        )}
        {job.source && (
          <span className="postit-tag postit-tag-source">{job.source}</span>
        )}
      </div>

      {salary && <div className="postit-salary">{salary}</div>}
      {job.stage && <div className="postit-stage">{job.stage}</div>}

      <div className="postit-footer">
        <span className="postit-date">{fmtDate(job.applicationDate)}</span>
        {job.nextStep && (
          <span className="postit-nextstep" title={job.nextStep}>
            → {job.nextStep.substring(0, 26)}{job.nextStep.length > 26 ? '…' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default PinBoard;
